<template>
  <account-home
    :tabs="tabs"
  >
    <brazil-form-v2
      ref="form"
      :validator="identityVerificationValidator"
      @submit="submissionEventHandler"
      @error="errorEventHandler"
    >
      <div
        v-if="!identity_verification"
        class="direct-debit-opt-in"
      >
        <brazil-checkbox-field
          name="direct_debit_opt_in_consent"
          class="direct_debit-opt-in-consent"
          input-id="direct_debit_opt_in"
          :label="directDebitText"
          :true-value="true"
          :false-value="false"
          :validator-field="$v.direct_debit_opt_in"
        />
      </div>
      <lgpd-identity-verification
        v-if="identity_verification"
        v-model="lgpdIdentityVerificationFields"
        :validator-field="$v.lgpdIdentityVerificationFields"
      />
      <nav-buttons
        ref="navButtons"
        :loading="formLoading"
        :prev-fn="prevFn"
        :prev-text="$t('pages.direct_debit_opt_in.prev_text')"
        :submit-text="submitText"
        :submit-disabled="disableSubmit"
      />
    </brazil-form-v2>
    <brazil-v-dialog
      v-if="dialog"
      id="direct-debit-dialog"
      v-model="dialog"
      width="600px"
    >
      <div id="direct-debit-dialog-container">
        <p
          id="direct-debit-dialog-instructions"
          v-html="directDebitDialogText"
        />
        <div class="button-container">
          <brazil-v-btn
            class="secondary-button"
            @click="dialogSecondaryHandler"
          >
            {{ $t('pages.direct_debit_opt_in.dialog_back_button_text') }}
          </brazil-v-btn>
          <brazil-v-btn
            class="primary-button"
            @click="dialogPrimaryHandler"
          >
            {{ $t('pages.direct_debit_opt_in.dialog_next_button_text') }}
          </brazil-v-btn>
        </div>
      </div>
    </brazil-v-dialog>
  </account-home>
</template>

<script>
import { call, get } from 'vuex-pathify';
import AccountHome from '../components/AccountHome.vue';
import BrazilCheckboxField from '../components/fields/BrazilCheckboxField.vue';
import currencyReals from '../filters/currencyReals';
import BrazilVBtn from '../components/BrazilVBtn.vue';
import BrazilVDialog from '../components/BrazilVDialog.vue';
import BrazilFormV2 from '../components/BrazilFormV2.vue';
import NavButtons from '../components/NavButtons.vue';
import LgpdIdentityVerification, {
  lgpdIdentityVerificationDefaultValues,
  lgpdIdentityVerificationValidator,
} from '../components/LgpdIdentityVerification.vue';
import captureRenderBlockingException from '../tracking/helpers/captureRenderBlockingException';
import captureSentryException from '../tracking/sentry/captureSentryException';

export default {
  components: {
    BrazilCheckboxField,
    AccountHome,
    BrazilVBtn,
    BrazilVDialog,
    BrazilFormV2,
    NavButtons,
    LgpdIdentityVerification,
  },
  data() {
    return {
      formLoading:           false,
      direct_debit_opt_in:   false,
      overdraft_opt_in:      false,
      dialog:                false,
      identity_verification: false,
      tabs:                  [
        { name: this.$t('pages.account_home.tabs.loan_applications'), url: '/loan_applications' },
        { name: this.$t('pages.account_home.tabs.opt_in'), url: '/loan_application/opt_in' },
      ],
      lgpdIdentityVerificationFields: lgpdIdentityVerificationDefaultValues,
    };
  },
  computed: {
    loanApplication: get('loanApplication'),
    directDebitText() {
      const loanApplicationId = this.loanApplication.loan_application_id;
      const primaryQuoteAmount = Number(this.filteredOffers[0].first_installments_amount);
      const lastQuote = this.filteredOffers[this.filteredOffers.length - 1];
      const secondaryQuoteAmount = Number(lastQuote.first_installments_amount);

      let text = this.$t('pages.direct_debit_opt_in.direct_debit_label');
      if (primaryQuoteAmount === secondaryQuoteAmount) {
        text = this.$t('pages.direct_debit_opt_in.direct_debit_label_refi');
      }

      text = this.lodash.replace(text, '[xyz]', loanApplicationId);
      text = this.lodash.replace(text, '[BBB]', currencyReals(primaryQuoteAmount));
      text = this.lodash.replace(text, '[AAA]', currencyReals(secondaryQuoteAmount));

      return text;
    },
    directDebitDialogText() {
      const loanApplicationId = this.loanApplication.loan_application_id;
      const primaryQuoteAmount = Number(this.filteredOffers[0].first_installments_amount);
      const lastQuote = this.filteredOffers[this.filteredOffers.length - 1];
      const secondaryQuoteAmount = Number(lastQuote.first_installments_amount);

      let text = this.$t('pages.direct_debit_opt_in.dialog_body');
      if (primaryQuoteAmount === secondaryQuoteAmount) {
        text = this.$t('pages.direct_debit_opt_in.dialog_body_refi');
      }

      text = this.lodash.replace(text, '[XYZ]', loanApplicationId);
      text = this.lodash.replace(text, '[XXX]', currencyReals(primaryQuoteAmount));
      text = this.lodash.replace(text, '[YYY]', currencyReals(secondaryQuoteAmount));

      return text;
    },
    loanAgreementOffer() {
      return this.lodash.find(
        this.loanApplication.loan_offers,
        (offer) => offer.loan_offer_id === this.loanApplication.loan_agreement.loan_offer_id,
      );
    },
    filteredOffers() {
      try {
        const filterParams = {
          min_amount:       this.loanAgreementOffer.min_amount,
          num_installments: this.loanAgreementOffer.num_installments,
        };

        const quotes = this.lodash.filter(this.loanApplication.loan_offers, filterParams);
        return this.lodash.sortBy(quotes, 'annual_interest_rate');
      } catch (e) {
        this.flash().destroyAll();
        this.flash(this.$t('shared.generic_error'), 'error');
        const extras = {
          loanApplication:    this.loanApplication,
          loanAgreementOffer: this.loanAgreementOffer,
        };
        captureRenderBlockingException(e, extras);
        return null;
      }
    },
    prevFn() {
      if (this.identity_verification) {
        return () => { this.identity_verification = false; };
      }

      return null;
    },
    submitText() {
      if (this.identity_verification) {
        return this.$t('pages.direct_debit_opt_in.submit_text');
      }
      return this.$t('pages.direct_debit_opt_in.next_text');
    },
    identityVerificationValidator() {
      if (this.identity_verification) {
        return this.$v;
      }
      return null;
    },
    disableSubmit() {
      return this.direct_debit_opt_in === this.loanApplication.opt_in_status.direct_debit_opt_in;
    },
  },
  mounted() {
    this.direct_debit_opt_in = this.loanApplication.opt_in_status.direct_debit_opt_in;
    this.overdraft_opt_in = this.loanApplication.opt_in_status.overdraft_opt_in;
  },
  methods: {
    submitOptInStatus: call('registration/submitOptInStatus'),
    async handleOptOut() {
      if (!this.identity_verification) {
        this.dialog = true;
        return;
      }
      try {
        const response = await this.submit();
        if (response.status !== 201) {
          this.handleResponse(response);
        } else {
          this.flash(this.$t('pages.direct_debit_opt_in.flash_success'), 'success');
        }
      } catch (e) {
        captureSentryException(e);
        this.flash(this.$t('pages.direct_debit_opt_in.flash_error'), 'error');
      }
    },
    async submit() {
      try {
        this.formLoading = true;
        const payload = {
          full_name:           this.lgpdIdentityVerificationFields.full_name,
          mother_name:         this.lgpdIdentityVerificationFields.mother_name,
          date_of_birth:       this.lgpdIdentityVerificationFields.date_of_birth,
          cpf:                 this.lgpdIdentityVerificationFields.cpf.replace(/\D/g, ''),
          direct_debit_opt_in: this.direct_debit_opt_in,
          overdraft_opt_in:    this.direct_debit_opt_in,
        };

        return await this.submitOptInStatus(payload);
      } catch (e) {
        captureSentryException(e);
        return e.response;
      } finally {
        this.formLoading = false;
      }
    },
    handleInvalidfields(invalidFields, errorMsgs) {
      this.$v.$reset();
      this.flash(errorMsgs.join('<br>'), 'error');
    },
    dialogPrimaryHandler() {
      this.direct_debit_opt_in = true;
      this.dialog = false;
    },
    dialogSecondaryHandler() {
      this.dialog = false;
      this.$v.$reset();
      this.identity_verification = true;
    },
    async submissionEventHandler() {
      this.flash().destroyAll();

      if (this.direct_debit_opt_in) {
        try {
          const response = await this.submit();
          this.handleResponse(response);
        } catch (e) {
          captureSentryException(e);
          this.flash(this.$t('pages.direct_debit_opt_in.flash_error'), 'error');
        }
      } else if (!this.identity_verification) {
        this.handleOptOut();
      } else {
        const response = await this.submit();
        this.handleResponse(response);
      }
    },
    async errorEventHandler() {
      this.flash().destroyAll();
      this.flash(this.$t('pages.direct_debit_opt_in.flash_error'), 'error');
    },
    handleResponse(resp) {
      try {
        if (resp.status === 201) {
          this.identity_verification = false;
          this.loanApplication.opt_in_status.direct_debit_opt_in = resp.data.direct_debit_opt_in;
          this.resetIdentityVerificationFields();
          this.flash(this.$t('pages.direct_debit_opt_in.flash_success'), 'success');
        } else {
          const invalidFields = resp.data.invalid_fields;
          const errorMsgs = resp.data.error_messages;
          if (errorMsgs != null && errorMsgs !== []) {
            this.handleInvalidfields(invalidFields, errorMsgs);
          } else {
            this.flash(this.$t('pages.direct_debit_opt_in.flash_error'), 'error');
          }
        }
      } catch (e) {
        this.flash(this.$t('pages.direct_debit_opt_in.flash_error'), 'error');
      }
    },
    resetIdentityVerificationFields() {
      Object.keys(this.lgpdIdentityVerificationFields).forEach((k) => {
        this.lgpdIdentityVerificationFields[k] = '';
      });
      this.$v.$reset();
    },
  },
  validations: {
    direct_debit_opt_in:            {},
    overdraft_opt_in:               {},
    lgpdIdentityVerificationFields: lgpdIdentityVerificationValidator,
  },
};
</script>

<style lang="scss" scoped>
  .brazil-form {
    min-height: 50vh;
  }
  #nav-button-container {
    margin-bottom: 2em;
  }
  .direct-debit-opt-in, .overdraft-opt-in {
    margin-top: 16px;
  }
  .opt-in-submit {
    margin-top: 16px;
    text-align: right;
  }
  #direct-debit-dialog, #overdraft-opt-in-dialog {
    padding-bottom: 0;
    font-size: 1.25em;
    font-size: 1em;
    #direct-debit-dialog-container, #overdraft-opt-in-dialog-container {
      padding: 2em;
    }
    #direct-debit-dialog-instructions, #overdraft-opt-in-dialog-instructions {
      line-height: 1.25;
      margin-bottom: 1em;
    }
    .button-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      flex-direction: column;
      button {
        white-space: normal;
        display: block;
        height: auto;
        padding: 0.5em 1em;
        margin-bottom: 1em;
        text-transform: initial;
      }
      .secondary-button {
        background-color: white;
        border: 1px solid green;
        color: green;
      }
      .primary-button {
        background-color: rgb(245, 191, 5);
        color: #4a4a4a;
      }
    }
  }
  @media screen and (min-width: 700px) {
    #submit-button {
      width: 200px;
      margin-right: 14px;
    }
    #direct-debit-dialog, #overdraft-opt-in-dialog {
      #direct-debit-dialog-container, #overdraft-opt-in-dialog-container {
        padding: 2.5em;
      }
      .button-container {
        flex-direction: row;
        button {
          padding: 0.5em 1em;
          margin: 1em;
          width: 45%;
        }
      }
    }
  }
</style>
